/**
 * WCC360 v2 — the canonical takeover.
 *
 * The on-device result appears instantly. This hook watches for the sealed
 * canonical report behind it and, the moment it exists, hands over: the
 * canonical payload becomes the authority and the client result is demoted
 * to a preview.
 *
 * It also surfaces, honestly:
 *   - answers still waiting to reach the database ("saved on this device"),
 *   - a failed pipeline stage, with a retry,
 *   - a run whose canonical report is blocked on a Command Center change.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getPipelineStatus, retryPipeline, type PipelineStatus } from "@/lib/enginePipeline";
import type { Band } from "./band";
import { fetchCanonicalResult, type CanonicalResult } from "./canonical";
import { readRunOutcome, type PipelineDisposition } from "./completion";
import { flushOutbox, outboxSize } from "./persistence";

const POLL_MS = 4000;
/** Stop polling after five minutes. A pipeline that slow has a real problem. */
const POLL_LIMIT = 75;

export interface CanonicalTakeover {
  /** Sealed report, once it exists. Until then the client preview stands. */
  canonical: CanonicalResult | null;
  /** True from the moment the canonical payload lands. */
  ready: boolean;
  status: PipelineStatus | null;
  disposition: PipelineDisposition | null;
  sessionId: string | null;
  /** Answers still on the device only. */
  pendingWrites: number;
  /** When the run was closed out, for the save confirmation. */
  completedAt: string | null;
  failedStage: string | null;
  errorMessage: string | null;
  retry: () => void;
  retrying: boolean;
}

export function useCanonicalTakeover(band: Band): CanonicalTakeover {
  const outcome = useRef(readRunOutcome(band)).current;
  const [canonical, setCanonical] = useState<CanonicalResult | null>(null);
  const [status, setStatus] = useState<PipelineStatus | null>(null);
  const [pendingWrites, setPendingWrites] = useState(() => outboxSize(band));
  const [retrying, setRetrying] = useState(false);
  const [nudge, setNudge] = useState(0);

  const sessionId = outcome?.sessionId ?? null;
  const disposition = outcome?.pipeline ?? null;

  // Keep pushing stranded answers while the learner reads their result.
  useEffect(() => {
    let alive = true;
    void (async () => {
      const left = await flushOutbox(band);
      if (alive) setPendingWrites(left);
    })();
    return () => { alive = false; };
  }, [band, nudge]);

  useEffect(() => {
    if (!sessionId) return;
    if (disposition !== "started") return;

    let alive = true;
    let ticks = 0;
    let timer: number | undefined;

    const tick = async () => {
      if (!alive) return;
      ticks++;

      const [s, c] = await Promise.all([
        getPipelineStatus(sessionId).catch(() => null),
        fetchCanonicalResult(outcome?.completedAt ?? null).catch(() => null),
      ]);
      if (!alive) return;
      if (s) setStatus(s);
      if (c) {
        setCanonical(c);
        return; // canonical has landed — nothing left to wait for
      }
      const failed = s && Object.values(s).some((v) => v === "failed");
      if (failed) return;
      if (ticks >= POLL_LIMIT) return;
      timer = window.setTimeout(tick, POLL_MS);
    };

    void tick();
    return () => { alive = false; if (timer) window.clearTimeout(timer); };
  }, [sessionId, disposition, outcome?.completedAt, nudge]);

  const retry = useCallback(() => {
    if (!sessionId) return;
    setRetrying(true);
    void retryPipeline(sessionId)
      .catch(() => undefined)
      .finally(() => {
        setRetrying(false);
        setNudge((n) => n + 1);   // restart polling
      });
  }, [sessionId]);

  const failedStage = status?.failed_stage ?? null;

  return {
    canonical,
    ready: canonical != null,
    status,
    disposition,
    sessionId,
    pendingWrites,
    completedAt: outcome?.completedAt ?? null,
    failedStage,
    errorMessage: status?.error_message ?? null,
    retry,
    retrying,
  };
}
